/**
 * Selectors for elements that may host the main scrollable content (Ionic or custom).
 */
const SCROLL_CONTAINER_SELECTORS = "[data-scroll-container], ion-content, .no-scrollbar";

export type ScrollableElement = HTMLElement & {
  scrollToTop?: (duration?: number) => void;
};

/**
 * Finds the main scrollable element in the document.
 * Prefers the first candidate that is actually scrollable (scrollHeight > clientHeight),
 * otherwise falls back to ion-content, then null.
 */
export function findScrollableElement(): ScrollableElement | null {
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(SCROLL_CONTAINER_SELECTORS));

  const scrolledElement = candidates.find((element) => element.scrollHeight > element.clientHeight);

  return (
    (scrolledElement as ScrollableElement | undefined) ??
    (document.querySelector<HTMLElement>("ion-content") as ScrollableElement | null) ??
    null
  );
}

/**
 * Scrolls the given scrollable area to the top.
 * Uses Ionic's scrollToTop when available (e.g. ion-content), otherwise
 * native scrollTo with smooth behaviour. If scrollElement is null, scrolls the window.
 */
export function scrollToTop(scrollElement: ScrollableElement | null): void {
  if (scrollElement?.scrollToTop) {
    scrollElement.scrollToTop(300);
    return;
  }

  if (scrollElement) {
    scrollElement.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  window.scrollTo({ top: 0, behavior: "smooth" });
}
